import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Button } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useKeycloak } from '@react-keycloak/web';
import { getUserResources } from '@dreebit-registry/vsm-microservice-components';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { BoltRounded } from '@mui-symbols-material/w300';

import IoTConnectionAdmin from '../IoTConnectionAdmin';
import IotSystemRegistrationModal from '../InstalledBaseTreeColumn/iotSystemRegistrationModal';

import HeroLead, { resolveHeroLeadState } from '../dashboardHero/HeroLead';
import HeroCustomerCard from '../dashboardHero/HeroCustomerCard';
import HeroServiceUnitCard from '../dashboardHero/HeroServiceUnitCard';
import FilesListWidget from '../dashboardHero/FilesListWidget';
import PressureLinesWidget from '../dashboardHero/PressureLinesWidget';
import { HeroBanner, HeroKpi, FeedGrid, FeedColumn } from '../dashboardHero/heroPrimitives';
import { HeroLeadSkeleton, fadeInSx } from '../dashboardHero/HeroSkeletons';

import { SystemDashboardHero } from '../../graphql/queries/systemDashboardHero';
import { SystemAssetListTotal } from '../../graphql/queries/systemAssetListTotal';
import { SystemStructureCounts } from '../../graphql/queries/systemStructureCounts';
import { SystemFilesTotal } from '../../graphql/queries/systemFilesTotal';
import { SystemNetworkInfo } from '../../graphql/queries/systemIoTConnectionWidget';
import { SystemIoTFirmware } from '../../graphql/queries/systemIoTFirmware';
import { isContractValid, isContractExpired } from '../../utils/isContractValid';
import { IndustryType } from '../../hooks/useIndustryType';
import { isPlcSystemType } from '../../config/systemTypes';
import { useIotAttachGate } from '../../hooks/useIotAttachGate';
import NetworkScanGateReason from '../NetworkScanGateReason';

interface Props {
    systemId: string;
    systemType: string | null;
    hasIotConnection: boolean;
    iotConnectionLoading: boolean;
    isIoTAdmin: boolean;
    compact?: boolean;
    /** Hero banner + admin contract bars only — used when the IoT-portal iframe renders below. */
    heroOnly?: boolean;
}

const SystemDashboard = ({ systemId, systemType, hasIotConnection, isIoTAdmin, compact, heroOnly }: Props) => {
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const administrateCustomers = getUserResources(keycloak.token, 'otto_crm').includes('administrateCustomers');
    const [iotSystemModalOpen, setIotSystemModalOpen] = useState(false);

    // One round trip decides the hero lead state (contract + connection status).
    // Skeleton only on first load — revisits paint from cache and refresh silently.
    const heroQuery = useQuery(SystemDashboardHero, { variables: { id: systemId }, fetchPolicy: 'cache-and-network' });
    const heroPending = heroQuery.loading && !heroQuery.data;
    const contractData = _.get(heroQuery, 'data.system.client.contract');
    const clientId = _.get(heroQuery, 'data.system.client.id');
    const industryType = _.get(heroQuery, 'data.system.client.industryType');
    const isSemiconductor = industryType === IndustryType.SEMICONDUCTOR;
    // Device presence is the union of the hero query and the route's own query
    // (hasIotConnection prop): the union can only add detection, so the lead never
    // shows a "no device" state for a live device if either source knows better —
    // the heroPending gate still prevents any flash before the hero settles.
    const iotConnected = !!_.get(heroQuery, 'data.system.iotConnection.id') || hasIotConnection;
    const contractValid = isContractValid(contractData);
    const contractExpired = isContractExpired(contractData);
    const state = resolveHeroLeadState({ contractValid, contractExpired, hasIotConnection: iotConnected });

    // Network scan gate (IBM-1039): where the hero would otherwise invite to
    // connect, a missing / failed effective scan takes the CTA's place — the
    // lead says why and the actions row shows the state and the way into the
    // installed base. Not requested while the flag is off or once connected.
    // Mirrors where connecting is offered today: typed PLC systems get the
    // registration iframe whatever the industry (systemDetails), unconfigured
    // systems the hero CTA for non-semiconductor clients only.
    const iotGate = useIotAttachGate({ kind: 'system', id: systemId, skip: iotConnected });
    const connectOffered = isPlcSystemType(systemType) || (!systemType && !isSemiconductor);
    const scanBlocked = !heroPending && contractValid && !iotConnected && connectOffered && iotGate.reason === 'networkScan';

    const assetTotalQuery = useQuery(SystemAssetListTotal, { variables: { id: systemId } });
    const assetTotal = _.get(assetTotalQuery, 'data.system.assetList.total', 0) ?? 0;
    // Structural limits (IBM-462). max -1 = no upper limit → plain count, no "/ …".
    const maxAssets = _.get(assetTotalQuery, 'data.system.systemLimits.maxAssets');
    const maxPressureLines = _.get(assetTotalQuery, 'data.system.systemLimits.maxPressureLines');
    const withLimit = (count: number, max: number | undefined | null) =>
        max != null && max !== -1 ? `${count} / ${max}` : count;

    const structureQuery = useQuery(SystemStructureCounts, { variables: { id: systemId } });
    const pressureLineTotal = (_.get(structureQuery, 'data.system.installedBaseNode.nodeRules.childRules', []) ?? [])
        .find((rule: any) => rule.nodeTypeKey === 'PRESSURE_LINE')?.currentNodeCount ?? 0;

    const filesTotalQuery = useQuery(SystemFilesTotal, { variables: { id: systemId } });
    const filesTotal = _.get(filesTotalQuery, 'data.system.installedBaseNode.fileList.total', 0) ?? 0;

    // Live connection status ships with the hero query; only the slow network
    // information stays a separate request so it can stream in after the lead.
    // The skip deliberately uses the prop: it can settle before the hero query,
    // starting the slowest request as early as possible.
    const showLive = iotConnected && !isSemiconductor;
    // no-cache: see SystemNetworkInfo — a failed lookup nulls the whole iotConnection.
    const networkQuery = useQuery(SystemNetworkInfo, { variables: { id: systemId }, skip: !showLive, fetchPolicy: 'no-cache' });
    const connectionStatus = _.get(heroQuery, 'data.system.iotConnection.connectionStatus');
    const networkStandard = _.get(networkQuery, 'data.system.iotConnection.networkInformation.currentStandard');
    const networkProvider = _.get(networkQuery, 'data.system.iotConnection.networkInformation.networkProvider');
    const signalStrength = _.get(networkQuery, 'data.system.iotConnection.networkInformation.signalStrength');
    const networkInfoLoading = showLive && networkQuery.loading && !networkQuery.data;

    // Connection and modem/gateway firmware for the lead's firmware line. The
    // fields can't join the cached hero/network queries: the backend returns
    // gateway objects with an empty id when firmwareVersion is selected, which
    // breaks cache normalization — hence the dedicated no-cache request.
    const firmwareQuery = useQuery(SystemIoTFirmware, {
        variables: { id: systemId },
        skip: !showLive,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });
    const firmwareVersion = _.get(firmwareQuery, 'data.system.iotConnection.firmwareVersion');
    const modemFirmwareVersion = _.get(firmwareQuery, 'data.system.iotConnection.gateway.firmwareVersion');
    const firmwareLoading = showLive && firmwareQuery.loading && !firmwareQuery.data;

    // Admin CTAs: add a contract (none yet — also for already-connected devices) or
    // extend an expired one. Industry only.
    // All state-dependent chrome is held back until the hero query settles so nothing flashes.
    const showAddContractBar = !heroPending && !isSemiconductor && administrateCustomers && isIoTAdmin && !contractValid && !contractExpired;
    const showExtendContractBar = !heroPending && !isSemiconductor && administrateCustomers && isIoTAdmin && contractExpired;
    // Connecting only starts from an unconfigured system (null systemType) via the
    // type picker; configured systems (NON_PLC or PLC) get no hero connect CTA. In
    // heroOnly mode the registration iframe renders directly below the hero anyway.
    const showConnect = !heroPending && !isSemiconductor && isIoTAdmin && contractValid && !iotConnected && !heroOnly && !systemType && !scanBlocked;

    const handleConnect = () => setIotSystemModalOpen(true);

    // The lead is gated on the hero query — a wrong state (e.g. "Not connected"
    // flashing before the connection status arrives) is worse than a skeleton.
    const lead = heroPending ? (
        <HeroLeadSkeleton compact={compact} withMetaLine={showLive} />
    ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, ...fadeInSx }}>
            <HeroLead
                state={state}
                isAdmin={isIoTAdmin}
                hasIotConnection={iotConnected}
                isSystem
                connectionStatus={connectionStatus}
                hubOnline={iotConnected}
                networkStandard={networkStandard}
                networkProvider={networkProvider}
                signalStrength={signalStrength}
                networkInfoLoading={networkInfoLoading}
                firmwareVersion={firmwareVersion}
                modemFirmwareVersion={modemFirmwareVersion}
                firmwareLoading={firmwareLoading}
                networkScanBlocked={scanBlocked}
            />
        </Box>
    );

    const kpis = (
        <>
            <HeroKpi value={withLimit(assetTotal, maxAssets)} label={t('Assets')} loading={assetTotalQuery.loading && !assetTotalQuery.data} />
            <HeroKpi value={withLimit(pressureLineTotal, maxPressureLines)} label={t('Pressure lines')} loading={structureQuery.loading && !structureQuery.data} />
            <HeroKpi value={filesTotal} label={t('Documents')} loading={filesTotalQuery.loading && !filesTotalQuery.data} />
        </>
    );

    const actions = scanBlocked ? (
        <NetworkScanGateReason
            variant="inline"
            scan={iotGate.scan}
            ownNodeId={iotGate.ownNodeId}
            pathNodeIds={iotGate.pathNodeIds}
        />
    ) : showConnect ? (
        <Button
            variant="solid"
            startDecorator={<BoltRounded />}
            onClick={handleConnect}
            sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.700' } }}
        >
            {t('Connect device')}
        </Button>
    ) : undefined;

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {showAddContractBar && (
                    <Box sx={{ mb: '16px' }}>
                        <IoTConnectionAdmin clientId={clientId} variant="add" />
                    </Box>
                )}
                {showExtendContractBar && (
                    <Box sx={{ mb: '16px' }}>
                        <IoTConnectionAdmin clientId={clientId} variant="extend" />
                    </Box>
                )}

                <HeroBanner lead={lead} kpis={kpis} actions={actions} />

                {!heroOnly && (
                    <>
                        <PressureLinesWidget systemId={systemId} />

                        <FeedGrid compact={compact}>
                            <FeedColumn>
                                <HeroCustomerCard variant="system" id={systemId} nodeId={systemId} />
                                <HeroServiceUnitCard nodeId={systemId} />
                            </FeedColumn>
                            <FeedColumn>
                                <FilesListWidget variant="system" id={systemId} compact={compact} />
                            </FeedColumn>
                        </FeedGrid>
                    </>
                )}
            </Box>

            {iotSystemModalOpen && (
                <IotSystemRegistrationModal
                    open={iotSystemModalOpen}
                    onClose={() => setIotSystemModalOpen(false)}
                    node={{ id: systemId, title: '', system: { id: systemId } }}
                />
            )}
        </>
    );
};

export default SystemDashboard;
